import {createAsyncThunk} from '@reduxjs/toolkit';
import {apiFetch, getWorkerApiUrl} from '../../lib/api';
import {ApiError, toApiError, toNetworkError} from '../../lib/apiError';
import {debugLog} from '../../lib/debug';
import {clearPendingSignup, writePendingSignup} from '../../lib/signupSession';
import type {
  SignupStartRequest,
  SignupStartResponse,
  SignupStatusResponse,
  SignupResendLinkRequest,
  SignupResendLinkResponse,
} from '../../backend-types/signup-api';

/**
 * Signup runs through Paddle, not Supabase — `supabase.auth.signUp()` is refused at the auth layer,
 * so there is no client-side path to an account that skips the checkout. These three endpoints are
 * all public: at the point they are called there is no session to authenticate with, by design.
 *
 * The order is inverted from the usual: card first, account second. See
 * `backend-types/signup-api.ts` for the whole flow.
 */

/** Every signup endpoint answers `{error, code}`; branch on `code`, never on the sentence. */
async function publicJson(path: string, init?: {method: string; body?: string}) {
  const headers: Record<string, string> = {};
  if (init?.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await apiFetch(`${getWorkerApiUrl()}${path}`, {
    method: init?.method ?? 'GET',
    headers,
    body: init?.body,
  });

  return {ok: response.ok, status: response.status, text: await response.text()};
}

/**
 * Park the email address and mint the token that ties the coming checkout to it.
 *
 * Creates nothing — no account, no Paddle customer, no charge — so an abandoned signup leaves
 * nothing behind to clean up. Rate limited to 10/hour per IP.
 */
export const startSignup = createAsyncThunk<
  {token: string; email: string; name: string; expiresAt: string},
  {email: string; name: string},
  {rejectValue: ApiError}
>(
  'signup/start',
  async ({email, name}, {rejectWithValue}) => {
    try {
      const body: SignupStartRequest = {email, name};
      const result = await publicJson('/rest/v1/signup/start', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (!result.ok) {
        return rejectWithValue(toApiError(result.status, result.text, 'Could not start signup'));
      }

      const parsed: SignupStartResponse = JSON.parse(result.text);
      debugLog('[Signup] Token minted for', parsed.email, 'expires', parsed.expires_at);

      // Persisted before checkout opens: a reload while the overlay is up must not strand someone
      // who is about to pay, or has just paid.
      writePendingSignup({
        token: parsed.signup_token,
        email: parsed.email,
        name,
        expiresAt: parsed.expires_at,
        paid: false,
      });

      return {token: parsed.signup_token, email: parsed.email, name, expiresAt: parsed.expires_at};
    } catch (error) {
      return rejectWithValue(toNetworkError(error, 'Could not start signup'));
    }
  }
);

/**
 * Has the subscription webhook turned the checkout into an account yet?
 *
 * Normally flips within seconds. If Paddle's webhook is lost entirely a cron backstop picks it up,
 * which can take up to five minutes — so the caller keeps polling rather than giving up. `failed` is
 * retried automatically by the same cron, so it is also a keep-polling state.
 */
export const pollSignupStatus = createAsyncThunk<
  {status: SignupStatusResponse['status']; email: string; loginLinkSentAt: string | null; expiresAt: string},
  string,
  {rejectValue: ApiError}
>(
  'signup/status',
  async (token, {rejectWithValue}) => {
    try {
      const result = await publicJson(`/rest/v1/signup/status?token=${encodeURIComponent(token)}`);

      if (!result.ok) {
        // A token the server has never heard of cannot be revived, and polling it forever would keep
        // a dead "setting up your account" spinner on screen. Drop it and let the screen say so.
        if (result.status === 404) {
          clearPendingSignup();
        }
        return rejectWithValue(toApiError(result.status, result.text, 'Could not check your signup'));
      }

      const parsed: SignupStatusResponse = JSON.parse(result.text);
      return {
        status: parsed.status,
        email: parsed.email,
        loginLinkSentAt: parsed.login_link_sent_at,
        expiresAt: parsed.expires_at,
      };
    } catch (error) {
      return rejectWithValue(toNetworkError(error, 'Could not check your signup'));
    }
  }
);

/**
 * Resend the first login link. Only valid once the signup is `provisioned`, and throttled to one
 * send per minute — a 429 carries `retryAfterSeconds`, which the screen counts down.
 *
 * The link only ever goes to the address stored on the signup, so holding the token cannot redirect
 * it somewhere else.
 */
export const resendSignupLink = createAsyncThunk<
  string,
  string,
  {rejectValue: ApiError}
>(
  'signup/resendLink',
  async (token, {rejectWithValue}) => {
    try {
      const body: SignupResendLinkRequest = {token};
      const result = await publicJson('/rest/v1/signup/resend-link', {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (!result.ok) {
        return rejectWithValue(toApiError(result.status, result.text, 'Could not resend the link'));
      }

      const parsed: SignupResendLinkResponse = JSON.parse(result.text);
      debugLog('[Signup] Login link resent to', parsed.email);
      return parsed.email;
    } catch (error) {
      return rejectWithValue(toNetworkError(error, 'Could not resend the link'));
    }
  }
);

/** Throw the in-progress signup away — used when it expires, or when the visitor starts over. */
export const abandonSignup = createAsyncThunk('signup/abandon', async () => {
  clearPendingSignup();
  return null;
});
